import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { X, ChevronDown, Tag } from 'lucide-react';
import { getTags } from '../../actions/tagActions';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'align',
  'blockquote', 'code-block',
  'link', 'image', 'video'
];

const ReinforcementForm = ({ mode = 'new', reinforcement = null, onSubmit, onClose, tag: { tags }, getTags }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const tagDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    getTags();
  }, [getTags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const initializeForm = async () => {
      setIsLoading(true);
      try {
        if (reinforcement) {
          setTitle(reinforcement.title || '');
          setContent(reinforcement.content || '');
          setSelectedTags(reinforcement.tags ? reinforcement.tags.map(tag => tag._id) : []);
        } else {
          setTitle('');
          setContent('');
          setSelectedTags([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeForm();
  }, [reinforcement]);

  const isView = mode === 'view';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    
    onSubmit({
      title: title.trim(),
      content,
      tags: selectedTags
    });
  };

  const handleTagSearch = (searchTerm) => {
    setTagSearchTerm(searchTerm);
  };

  const filteredTags = tags.filter(tag =>
    tag.title.toLowerCase().includes(tagSearchTerm.toLowerCase()) &&
    !selectedTags.includes(tag._id)
  );

  const handleTagSelect = (tagId) => {
    if (!selectedTags.includes(tagId)) {
      setSelectedTags([...selectedTags, tagId]);
    }
    setTagSearchTerm('');
  };

  const handleTagRemove = (tagId) => {
    setSelectedTags(selectedTags.filter(id => id !== tagId));
  };

  const getTagById = (tagId) => {
    return tags.find(tag => tag._id === tagId);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4] mx-auto"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-[#054581]">
          {mode === 'new' && 'Add New Reinforcement'}
          {mode === 'edit' && 'Edit Reinforcement'}
          {mode === 'view' && 'View Reinforcement'}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-[#054581] mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            readOnly={isView}
            className={`w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors ${
              isView ? 'bg-gray-50' : ''
            }`}
            placeholder="Enter reinforcement title"
            required
            maxLength={200}
          />
          <div className="text-right text-sm text-gray-500 mt-1">
            {title.length}/200 characters
          </div>
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-[#054581] mb-2">
            Content <span className="text-red-500">*</span>
          </label>
          <div className="border border-[#1086a4] rounded-md overflow-hidden" style={{ height: '400px' }}>
            <ReactQuill
              value={content}
              onChange={setContent}
              readOnly={isView}
              modules={quillModules}
              formats={quillFormats}
              className="bg-[#f9fafb] h-full flex flex-col"
              placeholder="Write reinforcement content..."
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            />
            <style>
              {`
                .ql-container {
                  flex: 1;
                  overflow: auto;
                  height: auto !important;
                }
                .ql-toolbar {
                  position: sticky;
                  top: 0;
                  z-index: 1;
                  background: #f9fafb;
                }
              `}
            </style>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#054581] mb-2">
            Tags
          </label>
          {!isView && (
            <div className="relative" ref={tagDropdownRef}>
              <div
                onClick={() => setShowTagDropdown(true)}
                className="min-h-[42px] px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md cursor-text focus-within:ring-2 focus-within:ring-[#1086a4] focus-within:border-transparent transition-all"
              >
                <div className="flex flex-wrap items-center gap-1">
                  {selectedTags.map(tagId => {
                    const tag = getTagById(tagId);
                    return tag ? (
                      <span
                        key={tagId}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-20 text-[#1086a4] border border-[#1086a4] border-opacity-30"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag.title}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTagRemove(tagId);
                          }}
                          className="ml-1 text-[#1086a4] hover:text-[#054581] hover:bg-[#1086a4] hover:bg-opacity-20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ) : null;
                  })}
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={tagSearchTerm}
                    onChange={(e) => handleTagSearch(e.target.value)}
                    onFocus={() => setShowTagDropdown(true)}
                    placeholder={selectedTags.length === 0 ? "Search and select tags..." : "Search tags..."}
                    className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-sm"
                  />
                </div>
              </div>
              {showTagDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-[#1086a4] rounded-md shadow-lg max-h-48 overflow-auto">
                  {filteredTags.length > 0 ? (
                    filteredTags.map(tag => (
                      <button
                        key={tag._id}
                        type="button"
                        onClick={() => handleTagSelect(tag._id)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center"
                      >
                        <Tag className="w-3 h-3 mr-2 text-[#1086a4]" />
                        {tag.title}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-500 text-sm">
                      {tagSearchTerm ? `No tags found for "${tagSearchTerm}"` : 'No more tags available'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {isView && selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedTags.map(tagId => {
                const tag = getTagById(tagId);
                return tag ? (
                  <span
                    key={tagId}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag.title}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>

        {mode === 'view' && reinforcement?.createdBy && (
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Created By
            </label>
            <input
              type="text"
              value={reinforcement.createdBy}
              readOnly
              className="w-full px-3 py-2 bg-gray-50 border border-[#1086a4] rounded-md text-gray-900"
            />
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
          >
            Cancel
          </button>
          {!isView && (
            <button
              type="submit"
              disabled={!title.trim() || !content.trim()}
              className="flex-1 py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {mode === 'new' ? 'Add Reinforcement' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

const mapStateToProps = state => ({
  tag: state.tag
});

export default connect(mapStateToProps, { getTags })(ReinforcementForm);